"use client";

import { useState } from "react";

type SessionOption = {
  id: string;
  dateLabel: string;
  locationLabel: string;
  price: number;
  effectivePrice?: number;
  standardPrice?: number;
  earlyBirdPrice?: number | null;
  isEarlyBird?: boolean;
  earlyBirdDeadline?: string | null;
  earlyBirdDeadlineLabel?: string | null;
};

export function BookingForm({
  currencySymbol = "€",
  taxIdLabel = "VAT number",
  taxIdRequired = true,
  trainingTitle,
  sessions,
}: {
  currencySymbol?: string;
  taxIdLabel?: string;
  taxIdRequired?: boolean;
  trainingTitle: string;
  sessions: SessionOption[];
}) {
  const [form, setForm] = useState({
    sessionId: sessions[0]?.id || "",
    seats: 1,
    firstName: "",
    lastName: "",
    jobFunction: "",
    company: "",
    address: "",
    city: "",
    postalCode: "",
    email: "",
    vatNumber: "",
    phone: "",
  });
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [reference, setReference] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");

  const selected = sessions.find((s) => s.id === form.sessionId);

  const set = (k: string, v: string | number) => setForm({ ...form, [k]: v });

  const requiredFilled =
    form.sessionId &&
    form.firstName &&
    form.lastName &&
    form.jobFunction &&
    form.company &&
    form.address &&
    form.city &&
    form.postalCode &&
    form.email &&
    (taxIdRequired ? form.vatNumber : true);

  const handleSubmit = async () => {
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch("/api/booking", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        setReference(data.reference || "");
        setStatus("sent");
      } else {
        setErrorMsg(data.error || "Something went wrong.");
        setStatus("error");
      }
    } catch {
      setErrorMsg("Something went wrong. Please try again or email us directly.");
      setStatus("error");
    }
  };

  if (status === "sent") {
    return (
      <div className="bg-sh-panel rounded-lg p-8 text-center max-w-lg">
        <p className="font-serif text-lg text-sh-tx mb-2">Booking confirmed</p>
        <p className="font-sans text-sm text-sh-tx2 mb-1">
          Thank you. A confirmation has been sent to your email.
        </p>
        {reference && (
          <p className="font-mono text-[11px] text-sh-tx3">Reference: {reference}</p>
        )}
        <p className="font-sans text-[12px] text-sh-tx3 mt-3">
          We will issue an invoice to the details you provided.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Session picker */}
      <div>
        <label className="font-sans text-[11px] font-medium text-sh-tx2 block mb-1.5">
          Select a session *
        </label>
        {sessions.length === 1 ? (
          <div className="w-full px-3.5 py-2.5 border border-black/[0.06] rounded-lg font-sans text-[13px] text-sh-tx bg-sh-soft">
            {sessions[0].dateLabel} — {sessions[0].locationLabel}
          </div>
        ) : (
          <select
            value={form.sessionId}
            onChange={(e) => set("sessionId", e.target.value)}
            className="w-full px-3.5 py-2.5 border border-black/[0.06] rounded-lg font-sans text-[13px] text-sh-tx bg-sh-bg outline-none"
          >
            {sessions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.dateLabel} — {s.locationLabel}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Fee line (from selected session) — early-bird aware */}
      {selected && (() => {
        const unit = selected.effectivePrice ?? selected.price;
        const std = selected.standardPrice ?? selected.price;
        const showEB = !!selected.isEarlyBird && selected.earlyBirdPrice != null && std > unit;
        const deadline = selected.earlyBirdDeadlineLabel ?? null;
        const fmt = (n: number) => n.toLocaleString("en-IN");
        return (
          <div className="border-y border-black/[0.06] py-3 space-y-1">
            <div className="flex items-baseline justify-between">
              <span className="font-sans text-[13px] text-sh-tx2">
                Fee {form.seats > 1 ? `(${form.seats} × ${currencySymbol}${fmt(unit)})` : "per participant"}
              </span>
              <span className="font-serif text-lg text-sh-tx">
                {showEB && (
                  <span className="font-sans text-[13px] text-sh-tx3 line-through mr-2">{currencySymbol}{fmt(std * form.seats)}</span>
                )}
                {currencySymbol}{fmt(unit * form.seats)}
              </span>
            </div>
            {showEB && deadline && (
              <p className="font-sans text-[11px] text-sh-red m-0">
                Early-bird rate — book by {deadline}. Standard rate {currencySymbol}{fmt(std)} thereafter.
              </p>
            )}
          </div>
        );
      })()}

      <div className="grid grid-cols-2 gap-4">
        <Field label="First name" value={form.firstName} onChange={(v) => set("firstName", v)} required />
        <Field label="Last name" value={form.lastName} onChange={(v) => set("lastName", v)} required />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Job function" value={form.jobFunction} onChange={(v) => set("jobFunction", v)} required />
        <Field label="Company" value={form.company} onChange={(v) => set("company", v)} required />
      </div>
      <Field label="Address" value={form.address} onChange={(v) => set("address", v)} required />
      <div className="grid grid-cols-[1fr_2fr] gap-4">
        <Field label="Postal code" value={form.postalCode} onChange={(v) => set("postalCode", v)} required />
        <Field label="City" value={form.city} onChange={(v) => set("city", v)} required />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Email" value={form.email} onChange={(v) => set("email", v)} type="email" required />
        <Field label={taxIdLabel} value={form.vatNumber} onChange={(v) => set("vatNumber", v)} required={taxIdRequired} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Phone (optional)" value={form.phone} onChange={(v) => set("phone", v)} />
        <div>
          <label className="font-sans text-[11px] font-medium text-sh-tx2 block mb-1.5">
            Number of participants *
          </label>
          <input
            type="number"
            min={1}
            value={form.seats}
            onChange={(e) => set("seats", Math.max(1, Number(e.target.value) || 1))}
            className="w-full px-3.5 py-2.5 border border-black/[0.06] rounded-lg font-sans text-[13px] text-sh-tx bg-sh-bg outline-none"
          />
        </div>
      </div>

      {status === "error" && (
        <p className="font-sans text-xs text-sh-red">{errorMsg}</p>
      )}

      <button
        onClick={handleSubmit}
        disabled={status === "sending" || !requiredFilled}
        className="font-sans text-[13px] font-semibold bg-sh-red text-white border-none py-3 px-8 rounded-3xl cursor-pointer disabled:opacity-50"
      >
        {status === "sending" ? "Confirming..." : "Confirm booking"}
      </button>
      <p className="font-sans text-[11px] text-sh-tx3">
        No payment is taken now. We will issue an invoice to the details above.
      </p>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="font-sans text-[11px] font-medium text-sh-tx2 block mb-1.5">
        {label}
        {required && " *"}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3.5 py-2.5 border border-black/[0.06] rounded-lg font-sans text-[13px] text-sh-tx bg-sh-bg outline-none"
      />
    </div>
  );
}
